import { useCallback, useEffect, useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form'

import type {
  IssuerAreaSelectOption,
  IssuerOption,
} from '~/components/bidding/common/bidIssuer'
import {
  isAreaSelectableForIssuer,
  issuerAreaSelectOptions,
  selectedAreaOf,
} from '~/components/bidding/common/bidIssuer'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import {
  isAreaInCandidates,
  isAreaTradable,
} from '~/pages/SpotBidding/models/tradableArea'

type Params<TFieldValues extends FieldValues> = {
  /** 業務エリア設定で絞った候補 (AC-BSP-019、 `~/hooks/useBusinessAreaFilter`)。 */
  isDisabled?: boolean
  candidateAreaCodes: readonly AreaCode[]
  /** 取引不可エリア (AC-BSP-005b)。 未取得 (undefined) の間は制限しない。 */
  disabledAreas: ReadonlySet<AreaCode> | undefined
  /**
   * 候補外・取引不可でも残すエリア (編集中の札のエリア)。 開いた瞬間に別エリアへ
   * 化けるのを防ぐ。 選択肢・選択の解除・送信前の照合の 3 つが同じ指定を使うので、
   * 省略できない引数にしている。
   */
  keepAreaCodes: readonly AreaCode[]
  /**
   * エリアを入れる form の field。 参照が毎 render 変わると解除の effect が毎回走るので、
   * module 定数で渡す。
   */
  areaFieldNames: ReadonlyArray<Path<TFieldValues>>
  /**
   * 選択を解除する時に欄へ書く値。 Controller の欄は `AREA_UNSELECTED` を渡す
   * (`undefined` で空けると、 RHF が欄の mount 時の値を表示に出し続ける。 #10105)。
   * native select の欄は省略 (`undefined`)。
   */
  unselectedValue?: AreaCode
  getValues: UseFormGetValues<TFieldValues>
  setValue: UseFormSetValue<TFieldValues>
}

type Result = {
  /**
   * 選択中の発注主体区分でのエリアの選択肢 ([[issuerAreaSelectOptions]])。 取引可能なエリアと
   * `keepAreaCodes` を出し、 業務エリアの候補外は残して選べなくする (AC-BSP-005b)。
   */
  areaSelectOptionsFor: (
    issuer: IssuerOption | undefined,
    selectedArea: AreaCode | undefined,
  ) => IssuerAreaSelectOption[]
  /**
   * 業務エリアの候補外 (`keepAreaCodes` を除く) のエリアの選択を解除する。 保存の直前
   * (`handleSubmit` の前) に呼ぶ。 複製・再入札で開いた時に reset で入ったエリアは、
   * エリアの select が後から form に登録される時に reset の値へ戻るため、 候補が
   * 変わった時の解除だけでは残る。
   */
  clearAreasOutsideCandidates: () => void
  /**
   * 送信直前の照合。 取引可能エリアの取得完了前に選んだ値は、 後から選択肢から消えても
   * form に残るため、 選択肢だけに頼らず送信時にも照合する。 業務エリアの候補外の値は
   * `clearAreasOutsideCandidates` で外すので、 ここは取引可否だけを見る。
   */
  isAreaTradable: (areaCode: AreaCode) => boolean
  /**
   * 発注主体区分を切り替えた時に呼ぶ。 選択中のエリアが新しい区分で選べなければ選択を
   * 解除する (AC-BSP-005b)。 区分が未選択 (undefined) なら解除しない。
   */
  clearAreasUnselectableForIssuer: (option: IssuerOption | undefined) => void
}

/**
 * 札を作るモーダル (個別作成 / コマクリックの札作成 / ブロック作成) が、 選択中のエリアを
 * 選べるエリアの集合に入れておくための手順を 1 か所に持つ: 選択肢の組み立て、
 * 選べなくなったエリアの選択解除、 送信直前の照合。
 *
 * 選択解除は、 欄を空欄に見せたまま選べないエリアで保存しないための手順
 * (AC-BSP-123 の「今は選べないエリアは空欄」、 AC-BSP-005b の発注主体切替時の
 * 選択解除)。 業務エリアの候補外は、 候補・`keepAreaCodes` が変わった時と保存の直前に
 * 解除し、 `keepAreaCodes` のエリアは解除しない。 発注主体区分で選べないエリアは、
 * 区分を切り替えた時に解除する。
 */
export function useSelectableAreas<TFieldValues extends FieldValues>({
  isDisabled = false,
  candidateAreaCodes,
  disabledAreas,
  keepAreaCodes,
  areaFieldNames,
  unselectedValue,
  getValues,
  setValue,
}: Params<TFieldValues>): Result {
  const tradableAreaCodes = useMemo(
    () =>
      JEPX_AREA_CODES.filter((code) =>
        isAreaTradable(code, disabledAreas, keepAreaCodes),
      ),
    [disabledAreas, keepAreaCodes],
  )
  const areaSelectOptionsFor = useCallback(
    (issuer: IssuerOption | undefined, selectedArea: AreaCode | undefined) =>
      issuerAreaSelectOptions(
        issuer,
        tradableAreaCodes,
        selectedArea,
        (area) => !isAreaInCandidates(area, candidateAreaCodes, keepAreaCodes),
        convertAreaLabel,
      ),
    [tradableAreaCodes, candidateAreaCodes, keepAreaCodes],
  )

  const clearAreasOutsideCandidates = useCallback(() => {
    for (const name of areaFieldNames) {
      const current = selectedAreaOf(getValues(name) as AreaCode | undefined)
      if (
        current !== undefined &&
        !isAreaInCandidates(current, candidateAreaCodes, keepAreaCodes)
      ) {
        setValue(name, unselectedValue as never)
      }
    }
  }, [
    areaFieldNames,
    candidateAreaCodes,
    keepAreaCodes,
    unselectedValue,
    getValues,
    setValue,
  ])

  useEffect(() => {
    if (!isDisabled) clearAreasOutsideCandidates()
  }, [clearAreasOutsideCandidates, isDisabled])

  const isAreaTradableNow = useCallback(
    (areaCode: AreaCode) =>
      isAreaTradable(areaCode, disabledAreas, keepAreaCodes),
    [disabledAreas, keepAreaCodes],
  )

  const clearAreasUnselectableForIssuer = useCallback(
    (option: IssuerOption | undefined) => {
      for (const name of areaFieldNames) {
        const current = selectedAreaOf(getValues(name) as AreaCode | undefined)
        if (
          current !== undefined &&
          !isAreaSelectableForIssuer(option, current)
        ) {
          setValue(name, unselectedValue as never)
        }
      }
    },
    [areaFieldNames, unselectedValue, getValues, setValue],
  )

  return {
    areaSelectOptionsFor,
    clearAreasOutsideCandidates,
    isAreaTradable: isAreaTradableNow,
    clearAreasUnselectableForIssuer,
  }
}
